import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { deepCopy, quickStartApi, setDocumentTitle } from 'js/utils';
import languageTypes from 'js/enums/language-types.enum';
import contextTypes from 'js/enums/context-types.enum';
import QuickStart from './quick-start';

const getParam = (name) =>
    new URLSearchParams(window.location.search).get(name) || '';

const formTemplate = {
    context_name: getParam('context_name'),
    type: contextTypes.DISPLAY,
    is_video: false,
    is_app: false,
    language: getParam('language') || languageTypes.EN,
    topics: [],
    keywords: [],
    integration: null,
    partner_id: '',
    contact_name: getParam('contact_name'),
    contact_email: getParam('contact_email'),
    contact_company: getParam('contact_company'),
    wants_insights: false,
    queries: [],
    utm_source: getParam('utm_source'),
};

const QuickStartPage = () => {
    const history = useHistory();

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [validating, setValidating] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(deepCopy(formTemplate));
    const [topics, setTopics] = useState([]);
    const [integrations, setIntegrations] = useState([]);
    const [termsAndConditions, setTermsAndConditions] = useState('');
    const [scaleScore, setScaleScore] = useState(null);
    const [scaleChanging, setScaleChanging] = useState(false);
    const [scaleError, setScaleError] = useState('');

    const resetForm = () =>
        setForm({ ...form, context_name: '', keywords: [], topics: [] });

    const loadData = async () => {
        setLoading(true);
        setErrors({});
        try {
            const {
                topics: topicsData,
                integrations: integrationsData,
                terms_and_conditions: termsAndConditionsData,
            } = await quickStartApi().load();
            setTopics(topicsData);
            setIntegrations(integrationsData);
            setTermsAndConditions(termsAndConditionsData);
        } catch (err) {
            setErrors({ loading: true });
        } finally {
            setLoading(false);
        }
    };

    const validate = async () => {
        setValidating(true);
        setErrors({});
        try {
            await quickStartApi().validate({
                integration: form.integration.id,
                partner_id: form.partner_id,
            });
        } catch (err) {
            setErrors({ validating: true });
            return false;
        } finally {
            setValidating(false);
        }
        return true;
    };

    const getScaleScore = async () => {
        setScaleError('');
        setScaleChanging(true);
        try {
            const { score } = await quickStartApi().scale({
                ...form,
                topics: form.topics.map(({ id }) => id),
            });
            setScaleScore(score);
        } catch (err) {
            setScaleScore(null);
            setScaleError('Unable To Calculate Scale');
        } finally {
            setScaleChanging(false);
        }
    };

    const submit = async () => {
        setSubmitting(true);
        setErrors({});
        try {
            await quickStartApi().submit({
                ...form,
                integration: form.integration.id,
                topics: form.topics.map(({ id }) => id),
            });
        } catch (errs) {
            setErrors(errs);
            throw errs;
        } finally {
            setSubmitting(false);
        }
    };

    useLayoutEffect(() => {
        if (window.location.search) {
            history.replace('/quick-start');
        }
        setDocumentTitle(['4D', 'Quick Start']);
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => setErrors({ validating: false }), [form.partner_id]);

    useEffect(
        () => setErrors(deepCopy({ ...errors, contact_name: undefined })),
        [form.contact_name], // eslint-disable-line react-hooks/exhaustive-deps
    );
    useEffect(
        () => setErrors(deepCopy({ ...errors, contact_company: undefined })),
        [form.contact_company], // eslint-disable-line react-hooks/exhaustive-deps
    );
    useEffect(
        () => setErrors(deepCopy({ ...errors, contact_email: undefined })),
        [form.contact_email], // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(() => {
        if (form.topics.length || form.keywords.length) {
            getScaleScore();
        } else {
            setScaleScore(null);
        }
    }, [form.is_video, form.language, form.topics, form.keywords]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <QuickStart
            form={form}
            loading={loading}
            submitting={submitting}
            validating={validating}
            errors={errors}
            topics={topics}
            integrations={integrations}
            termsAndConditions={termsAndConditions}
            scaleScore={scaleScore}
            scaleError={scaleError}
            scaleChanging={scaleChanging}
            onFormChange={setForm}
            onFormReset={resetForm}
            onReload={loadData}
            onSubmit={submit}
            onValidate={validate}
            onGetScaleScore={getScaleScore}
        />
    );
};

export default QuickStartPage;
